










































import { Component, Vue, Watch } from "vue-property-decorator";
import { VideoChunkDatabase, PdfChunkDatabase, Chunk } from "./indexedDb";
import Dexie from "dexie";
import { FetchRange } from "./fetchUtil";

import adultsThumbCs from "./assets/Lego_Adults_cs.png";
import adultsThumbSk from "./assets/Lego_Adults_sk.png";

import childThumbCs from "./assets/Lego_Kids_cs.png";
import childThumbSk from "./assets/Lego_Kids_sk.png";

@Component({})
export default class App extends Vue {
  private videoUrl = "";
  private videoMuted = false;
  private videoInterval = 60000;
  private videoRetryInterval = 1800;
  private inactivityTimer = -1;
  private timer = -1;
  private imageSequence: string[] = [];
  public videoSrc = "";
  public isScreenSaverRunning = false;
  public adultButtonThumbnail = adultsThumbCs;
  public childButtonThumbnail = childThumbCs;
  public iframeUrl = "";

  private catalog2Url = this.$t("catalog1Ur2") as string;
  private catalog2UrlCached = "";

  constructor() {
    super();

    const params = new URLSearchParams(window.location.search.slice(1));
    if (params.has("videoInterval")) {
      this.videoInterval =
        parseInt(params.get("videoInterval") || "") * 1000 || 60000;
    }
    if (params.has("videoRetryInterval")) {
      this.videoRetryInterval =
        parseInt(params.get("videoRetryInterval") || "") || 18000;
    }

    if (params.has("videoUrl")) {
      this.videoUrl = params.get("videoUrl") as string;
    }

    if (params.has("videoMuted")) {
      this.videoMuted =
        (params.get("videoMuted") as string) === "true" || false;
    }

    if (params.has("language")) {
      const lang = (params.get("language") as string) || "cs";

      if (lang === "sk") {
        this.$i18n.locale = "sk";
        this.adultButtonThumbnail = adultsThumbSk;
        this.childButtonThumbnail = childThumbSk;
      }
      this.catalog2Url = this.$t("catalog1Ur2") as string;
    }
  }

  async cacheCatalog2(): Promise<void> {
    const db = new PdfChunkDatabase();
    const pdfUrl = this.catalog2Url;

    // Odstraní všechny PDF, které nejsou aktuálně používané
    db.getAll().then(all => {
      all
        .filter(pdf => pdf.url !== pdfUrl)
        .map(pdf => pdf.url)
        .forEach(async url => {
          await db.remove(url);
        });
    });
    return db
      .get(pdfUrl)
      .then(async cachedPdf => {
        // Při změně URL smaže celou DB a tím uvolní místo
        if (cachedPdf && pdfUrl !== cachedPdf.url) {
          try {
            await db.clear();
            console.info("db clean");
          } catch (err) {
            console.error("clear older db");
          }
        }
        let eTag = "";
        if (cachedPdf?.eTag) {
          eTag = cachedPdf.eTag;
        }

        const remotePdf = await FetchRange.fetch(pdfUrl, eTag);
        let chunks: Chunk[] = [];
        const { status } = remotePdf;

        if (status === 200) {
          // Uložení staženého PDF
          await db.put(remotePdf);
          chunks = remotePdf.chunks;
          console.info("Catalog2 PDF downloaded.");
        } else if (status === 304) {
          // PDF z cache
          if (!cachedPdf) {
            throw new Error("Invalid cache with PDF");
          }
          console.info("Catalog2 PDF from cache.");
          chunks = cachedPdf.chunks;
        } else {
          throw new Error(`Fetch PDF ends with status: "${status}"`);
        }

        const buffer = chunks.map(ch => ch.blob);
        const url1 = URL.createObjectURL(
          new Blob(buffer, {
            type: "application/pdf"
          })
        );
        this.catalog2UrlCached = url1;
      })
      .catch(err => {
        console.error(err);
      });
  }

  async cacheVideo(url: string) {
    if (!url) {
      return;
    }
    const dbName = "dex-lego";
    const db = new VideoChunkDatabase(dbName, 1);

    db.open()
      // Kontrola a stazeni videa
      .then(async () => {
        if (!db.meta || !db.videoChunks) {
          throw new Error("db meta or videoChunks is undefined");
        }
        const cachedMeta = await db.meta
          .where("url")
          .equals(url)
          .first();
        if (cachedMeta && cachedMeta.url === url) {
          // Video je jiz nacachovane
          console.info(`${url} is already cached`);
          return;
        }

        // Video je potreba znovu stahnout
        console.info(`${url} needs to be downloaded`);

        // Odstrani stare zaznamy z aktuální databaze
        await db.meta.clear();
        await db.videoChunks.clear();

        const chunkSize = 4194304;
        let offset = 0;

        let res = await fetch(url, {
          method: "HEAD"
        });

        const contentLength = parseInt(
          res.headers.get("Content-Length") || "",
          10
        );
        if (contentLength < 1) {
          throw new Error(`Content-length is empty for ${url}`);
        }

        const chunkCount = Math.trunc(contentLength / chunkSize) + 1;
        for (let i = 1; i <= chunkCount; i++) {
          const from = offset;
          const to = offset + chunkSize;
          // console.log(`before downloading bytes=${from}-${to}`);

          res = await fetch(url, {
            headers: new Headers({
              Range: `bytes=${from}-${to}`
            })
          });
          const b = await res.blob();
          await db.videoChunks.add({ blob: b, order: i, to, from });
          offset += chunkSize + 1;
          // console.log(`after downloading bytes=${from}-${to}`);
        }
        await db.meta.add({
          url,
          chunkCount,
          chunkSize
        });
        return;
      })
      // Nacteni videa z DB
      .then(async () => {
        if (!db.meta || !db.videoChunks) {
          throw new Error("db meta or videoChunks is undefined");
        }

        const cachedMeta = await db.meta
          .where("url")
          .equals(url)
          .first();
        if (!cachedMeta) {
          throw new Error(`no cached meta for ${url}`);
        }
        const buffer = [];
        for (let i = 1; i <= cachedMeta.chunkCount; i++) {
          const part = (
            await db.videoChunks
              .where("order")
              .equals(i)
              .first()
          )?.blob;
          if (!part) {
            throw new Error(`videoChunk "${i}" not found.`);
          }
          buffer.push(part);
        }
        return new Blob(buffer);
      })
      .then((blob: Blob) => {
        const url = URL.createObjectURL(blob);
        this.videoSrc = url;
        // Zapnutí inactivity časovače, který kontroluje neaktivitu pouze u elementů, které nejsou v iframe.
        // Pokud se zobrazí iframe, tento časovač se vypne (destroyInactivityTime) a zapne se checkFocus.
        // Ten je aktivní tak dlouho, dokud je zobrazený iframe, pokud se zapne spořič, nebo se přijde zpět
        // na rozcestník, tak dojde k jeho vypnutí.
        this.inactivityTime();
        (document.getElementById(
          "video"
        ) as HTMLVideoElement).muted = this.videoMuted;
      })
      .catch(Dexie.MissingAPIError, e => {
        console.error("Couldn't find indexedDB API", e);
      })
      .catch("SecurityError", e => {
        console.error(
          "SeurityError - This browser doesn't like fiddling with indexedDB.",
          e
        );
      })
      .catch(async e => {
        console.error(e);
        if (e && e.name === "AbortError") {
          console.warn(
            `trying save video again after ${1000 *
            this.videoRetryInterval} seconds.`
          );
          db.close();
          const databases = await Dexie.getDatabaseNames();
          await Promise.all(
            databases.map(async name => {
              await Dexie.delete(name);
              return name;
            })
          );

          // Když se vykytne chyba, tak vyzkouším video uložit video znovu do DB.
          // Tato chyba nastává často, kdy se snažíme rychle po sobě uložit více nových videí,
          // protože po smazání obsahu v indexeddb stejně zůstane zabraná paměť zhruba dalších 10 minut.
          setTimeout(async () => {
            await this.cacheVideo(url);
          }, 1000 * this.videoRetryInterval);
        }
      });
  }

  private adultClick(event: Event) {
    this.destroyInactivityTime();
    event.preventDefault();
    event.stopPropagation();
    this.userInactivity = false;
    this.iframeUrl = this.$t("catalog1Url") as string;
    this.inactivityTimer = setInterval(() => {
      this.checkFocus("#web-viewer");
    }, 2000);
  }

  private childClick(event: Event) {
    this.destroyInactivityTime();
    event.preventDefault();
    event.stopPropagation();
    this.userInactivity = false;
    let url = this.catalog2Url;
    if (this.catalog2UrlCached) {
      url = this.catalog2UrlCached;
    }

    const params = new URLSearchParams();
    for (const i of this.imageSequence.slice(0)) {
      params.append("source", i);
    }
    this.iframeUrl = `pdf-viewer/index.html#${params.toString()}`;
    this.inactivityTimer = setInterval(() => {
      this.checkFocus("#web-viewer");
    }, 2000);
  }

  private goHome() {
    // zrušení timeut z checkFocus
    clearTimeout(this.timer);
    // zrušení intervalu checkFocus
    clearInterval(this.inactivityTimer);
    this.userInactivity = false;
    this.iframeUrl = "";
    this.inactivityTime();
  }

  mounted() {
    // Catalog 2 je PDF, proto je dobre si ho predstahovat dopredu
    this.cacheCatalog2();

    const sources: string[] = [];
    for (let i = 1; i <= 150; i += 1) {
      const index = i.toString().padStart(3, '0')
      sources.push(`https://http.office.ki-wi.cz/martin-web/lego-image-sequence/medium/2025_01-CZ-${index}.png`)
    }

    caches.open('dex-lego').then(async (cache) => {
      for (const source of sources) {
        const res = await cache.match(source);
        if (!res) {
          await cache.add(source)
        }
      }

      for (const source of sources) {
        const res = await cache.match(source);
        if (res) {
          this.imageSequence.push(URL.createObjectURL(await res.blob()))
        }
      }
    })



    // Stažení videa, které se má přehrávat ve spořiči obrazovky
    this.cacheVideo(this.videoUrl);

    ["touchstart", "mousedown"].forEach(evt => {
      document
        .getElementById("adult-button")
        ?.addEventListener(evt, this.adultClick.bind(this));
      document
        .getElementById("child-button")
        ?.addEventListener(evt, this.childClick.bind(this));

      document
        .getElementById("home-button")
        ?.addEventListener(evt, this.goHome.bind(this));

      document
        .getElementById("screen-saver")
        ?.addEventListener(evt, this.hideScreenSaver.bind(this));
    });
  }

  destroyed() {
    ["touchstart", "mousedown"].forEach(evt => {
      document
        .getElementById("adult-button")
        ?.removeEventListener(evt, this.adultClick.bind(this));
      document
        .getElementById("child-button")
        ?.removeEventListener(evt, this.childClick.bind(this));

      document
        .getElementById("home-buton")
        ?.removeEventListener(evt, this.goHome.bind(this));

      document
        .getElementById("screen-saver")
        ?.removeEventListener(evt, this.hideScreenSaver.bind(this));
    });

    URL.revokeObjectURL(this.videoSrc);
    clearInterval(this.inactivityTimer);
  }

  private resetTimer() {
    clearTimeout(this.inactivityTimer);
    this.inactivityTimer = setTimeout(this.showScreensaver, this.videoInterval);
    // 1000 milliseconds = 1 second
  }

  private inactivityTime() {
    this.resetTimer();
    // DOM Events
    document.addEventListener("click", this.resetTimer);
  }

  private destroyInactivityTime() {
    clearTimeout(this.inactivityTimer);

    window.removeEventListener("load", this.resetTimer);
    // DOM Events
    document.removeEventListener("click", this.resetTimer);
  }

  private userInactivity = false;

  private checkFocus(selector: string) {
    // Kontrola jestli ma iframe focus, pokud ano tak ho zavolam blur, zrusim casoc (v pri zmene userInactivity). Jinak nastavim userInactivity na true a spustim casovac.
    if (document.activeElement == document.querySelector(selector)) {
      this.userInactivity = false;
      const d = document.activeElement as HTMLElement;
      d.blur();
    } else {
      this.userInactivity = true;
    }
  }

  @Watch("userInactivity")
  onUserInactivityChanged(value: boolean) {
    if (value === false) {
      clearTimeout(this.timer);
    } else {
      this.timer = setTimeout(this.showScreensaver, this.videoInterval);
    }
  }

  private refreshIframe() {
    this.iframeUrl = "";
  }

  private showScreensaver() {
    if (!this.videoSrc) {
      // Pokud neni video definovane, tak udelam jenom reload iframe
      this.refreshIframe();
      this.userInactivity = false;
      return;
    }

    clearInterval(this.inactivityTimer);
    const video = document.querySelector("#video") as HTMLVideoElement;
    video
      .play()
      .then(() => {
        this.isScreenSaverRunning = true;
        this.refreshIframe();
      })
      .catch(err => {
        console.error(err);
        this.isScreenSaverRunning = false;
        this.userInactivity = false;
        this.inactivityTime();
      });
  }

  private hideScreenSaver(event: Event) {
    event.stopPropagation();
    event.preventDefault();
    const video = document.querySelector("#video") as HTMLVideoElement;
    video.pause();
    this.isScreenSaverRunning = false;
    this.userInactivity = false;
    this.inactivityTime();
  }
}
